import type { RunControlAction } from '../types/batch-history.api.types';
import type { RunProgress, StageProgress } from '../types/run.api.types';

import { latestStage } from './curation-funnel';
import { reviewLensSearch, type RunProductsSearch } from './run-products-search';

/** Stage statuses that mean the gate said no (the read-only caption reads "Rejected"). */
const REJECTED_STATUSES = new Set(['FAILED', 'RECONCILE_FAILED']);

/** Control-log actions that decide a gate — the decided panel's "who / when / what". */
const GATE_DECISION_ACTIONS = new Set(['GATE_APPROVE', 'GATE_REJECT', 'GATE_JUMP_BACK', 'SKIP']);

/** One entry of the decision panel's "Decisions" picker. */
export interface DecisionOption {
  stage: string;
  /** `open` = waiting on the user; `decided` = a gated stage already past; `results` = an
   *  ungated (or not yet reached) stage a deep link asked for — still shown, never dropped. */
  state: 'open' | 'decided' | 'results';
  /** The stage's newest attempt (null when the batch doesn't declare it). */
  progress: StageProgress | null;
}

/**
 * The batch's gates for the Decisions picker: the open one first, then the decided ones newest
 * first; a requested `picked` stage that is neither (ungated results, not reached) is appended so
 * the picker always names what the panel shows.
 */
export function decisionOptions(
  progress: RunProgress | undefined,
  picked: string | null,
): DecisionOption[] {
  if (!progress) return [];
  const openStage = progress.pendingGate?.stage ?? null;
  const codes = [...new Set(progress.stages.map((s) => s.stage))];
  const decided = codes
    .map((code) => latestStage(progress.stages, code))
    .filter(
      (s): s is StageProgress =>
        !!s && s.requiresGate && s.stage !== openStage && s.status !== 'PENDING',
    )
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''));
  const options: DecisionOption[] = [
    ...(openStage
      ? [
          {
            stage: openStage,
            state: 'open' as const,
            progress: latestStage(progress.stages, openStage),
          },
        ]
      : []),
    ...decided.map((s) => ({ stage: s.stage, state: 'decided' as const, progress: s })),
  ];
  if (picked && !options.some((o) => o.stage === picked)) {
    options.push({
      stage: picked,
      state: 'results',
      progress: latestStage(progress.stages, picked),
    });
  }
  return options;
}

/** Stages with no product ledger of their own (the funnel summary's `_PASS_THROUGH_STAGES`): their
 *  counters stay 0 until they close, so their cohort is the previous reached stage's. */
const PASS_THROUGH_STAGES = new Set(['APPROVAL', 'INSIGHTS']);

/**
 * How many products reached `stageCode` — the funnel summary's ① `in_`: a stage with its own
 * ledger reports `products_expected`; a pass-through one (APPROVAL / INSIGHTS) inherits what the
 * last reached stage before it passed on (`products_succeeded`). Null when nothing says.
 */
export function gateArrivedCount(
  progress: RunProgress | undefined,
  stageCode: string,
): number | null {
  const stages = progress?.stages ?? [];
  let prior: number | null = null;
  for (const code of new Set(stages.map((s) => s.stage))) {
    const stage = latestStage(stages, code);
    if (!stage) continue;
    if (code === stageCode) {
      // §87 — the backend's own arrivals (same projection as the funnel) win when sent.
      if (typeof stage.arrived === 'number') return stage.arrived;
      const expected = stage.counts?.products_expected ?? 0;
      return !PASS_THROUGH_STAGES.has(code) && expected > 0 ? expected : prior;
    }
    // Not-reached / skipped stages and other pass-throughs hand on the cohort unchanged.
    if (stage.status === 'PENDING' || stage.status === 'SKIPPED' || PASS_THROUGH_STAGES.has(code)) {
      continue;
    }
    prior = stage.counts?.products_succeeded ?? prior;
  }
  return null;
}

/** The `catalog-enrichment-run` key of an open generic gate's waiting line — "for your approval" at
 *  APPROVAL, "for your decision" elsewhere; the `Unknown` form when the cohort isn't known. */
export function gateWaitingKey(stageCode: string, arrived: number | null): string {
  const base = stageCode === 'APPROVAL' ? 'generic.waitingApproval' : 'generic.waitingDecision';
  return arrived === null ? `${base}Unknown` : base;
}

/** The outcome word key for a decided / finished stage (shared by the panel line and picker). */
export function resolvedOutcomeKey(stage: StageProgress): string {
  if (REJECTED_STATUSES.has(stage.status)) return 'gate.readOnlyRejected';
  if (stage.status === 'SKIPPED') return 'progressCard.decision.SKIPPED';
  // A gated stage only completes once approved; an ungated one just finished on its own.
  return stage.requiresGate ? 'gate.readOnlyApproved' : 'gate.readOnlyCompletedLabel';
}

/** The newest control-log decision on `stage` (who decided, when, what) — null if none logged. */
export function latestGateDecision(
  actions: readonly RunControlAction[],
  stage: string,
): RunControlAction | null {
  return (
    actions.find((a) => a.targetStage === stage && GATE_DECISION_ACTIONS.has(a.action)) ?? null
  );
}

/** Control-log action → the decision-bar action word key (`actions.*`). */
export function gateDecisionActionKey(action: string): string {
  switch (action) {
    case 'GATE_APPROVE':
      return 'actions.APPROVE';
    case 'GATE_REJECT':
      return 'actions.REJECT';
    case 'GATE_JUMP_BACK':
      return 'actions.JUMP_BACK';
    default:
      return 'actions.SKIP';
  }
}

/** Products search that opens the decision panel on `stage` (absent = the open gate). */
export function decisionSearch(stage?: string | null): Partial<RunProductsSearch> {
  return stage ? { decision: stage } : {};
}

/**
 * The old `/gate` URL → Products search (Option 2 keeps every old link alive): `?stage=` picks that
 * decision; a Content generation `?productId=` (explicit, or the open gate's) opens the review lens
 * in focus mode under the collapsed panel line. Any other stage ignores `productId`, as before.
 */
export function gateRedirectSearch(
  stage: string | null,
  productId: string | null,
  openStage: string | null,
): Partial<RunProductsSearch> {
  const target = stage ?? openStage;
  const base = decisionSearch(stage);
  if (productId && target === 'ENRICHMENT') {
    return {
      ...reviewLensSearch(),
      decision: 'ENRICHMENT',
      focus: productId,
      panel: 'collapsed',
    };
  }
  return base;
}
